import { removeMdnFormatting } from "@expert/common-ui";
import { Button, Flex, Input, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { IMaskInput } from "react-imask";
import { useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import { isBlocklisted } from "@/customer-panel/components/customer-verification/utils/isBlocklisted";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { formatPhoneNumber, validatePhoneNumber } from "@/shared-utils";
import classes from "./CustomerVerificationUpdated.module.css";

interface MdnSectionProps {
    initialMdn: string;
    isEditing: boolean;
    onComplete: (input: string) => void;
    onEditClick: () => void;
    shouldAutoFocus?: boolean;
    /**
     * Required key prop to reset component state when needed.
     * Pass a unique value (like session ID) to reset state.
     */
    key: string | number;
}

export function SimplifiedMdnSection({
    initialMdn,
    isEditing,
    onComplete,
    onEditClick,
    shouldAutoFocus = false,
}: MdnSectionProps): JSX.Element {
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const { dispatcher } = useAnalytics();
    const [inputMdn, setInputMdn] = useState<string>(initialMdn);
    const [error, setError] = useState<string | null>(null);
    const { blocklistedNumbers, blocklistedPrefixes } = usePartnerConfig();

    function onClickSave() {
        const strippedInputMdn = removeMdnFormatting(inputMdn);
        if (isBlocklisted(strippedInputMdn, blocklistedNumbers, blocklistedPrefixes)) {
            void dispatcher.dispatchBusinessEvent("BlocklistedMdnSubmitted");

            setError("This isn’t the customer’s phone number. Please ask for the right one.");
            return;
        }
        void dispatcher.dispatchBusinessEvent("MDN_Submitted");
        setError(null);
        onComplete(strippedInputMdn);
    }

    const onInputChange = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        setInputMdn(value);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && validatePhoneNumber(inputMdn).valid) {
            void dispatcher.dispatchUserAction("EnterKey", "MdnContinue");
            onClickSave();
        }
    };

    const phoneQuestionId = "phone-number-question";
    return (
        <Flex direction="column">
            <Text c="dark.1" size="sm" mb="md" mt="md" id={phoneQuestionId}>
                What’s the phone number they’re calling about?
            </Text>
            {isEditing ? (
                <>
                    <Input.Wrapper
                        data-testid="mdn-edit"
                        error={error}
                        className="fs-mask"
                        classNames={{
                            label: classes.label,
                        }}
                    >
                        <Input
                            aria-label="Phone Number"
                            aria-labelledby={phoneQuestionId}
                            aria-invalid={!!error}
                            id="phone-input"
                            autoFocus={shouldAutoFocus}
                            size="md"
                            value={inputMdn}
                            rightSectionPointerEvents="all"
                            rightSectionWidth={100}
                            component={IMaskInput}
                            onKeyDown={onKeyDown}
                            onInput={onInputChange}
                            mask="+1 (000) 000-0000"
                            onChange={(event) => {
                                setInputMdn(event.currentTarget.value);
                            }}
                            classNames={{ wrapper: classes.styledInputWrapper, input: classes.styledInput }}
                        />
                    </Input.Wrapper>
                    <ButtonWithAnalytics
                        size="sm"
                        fz="xs"
                        mt="sm"
                        disabled={inputMdn ? !validatePhoneNumber(inputMdn).valid : true}
                        onClick={onClickSave}
                        analytics={{ actionId: "SavePhoneNumber" }}
                        aria-label="Save phone number"
                    >
                        Save
                    </ButtonWithAnalytics>
                </>
            ) : (
                <Flex align="center" justify="space-between" aria-labelledby={phoneQuestionId}>
                    <Text
                        size="sm"
                        c="dark.1"
                        className="fs-mask"
                        id="saved-phone-number"
                        role="status"
                        aria-live="polite"
                    >
                        {formatPhoneNumber(inputMdn).number}
                    </Text>
                    <ButtonWithAnalytics
                        td="underline"
                        variant="subtle"
                        color="dark.1"
                        size="sm"
                        fz="xs"
                        onClick={onEditClick}
                        analytics={{ actionId: "change phone number" }}
                        aria-label="Change phone number"
                    >
                        Change
                    </ButtonWithAnalytics>
                </Flex>
            )}
        </Flex>
    );
}
